import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { X, ChevronDown } from 'lucide-react';

// Shared multi-select chip field — yellow chips, rotating chevron, checked-row tick,
// optional search. It was the private `SelectField` of ExportOrdersDrawer (ERP-5041)
// and mirrors FilterDrawer's "simpleList" markup; promoted here on 9 Oct so the
// Platform Pricing Import page (ERP-5249) reuses it instead of growing a second copy.
//
// Two layouts:
//   default      the box wraps and grows with the chips (min 44px) — ExportOrdersDrawer
//   fixedHeight  the box keeps a fixed height (px; default 40) and one line; chips that
//                do not fit are folded into "+N" (Figma "Filters": "Do not adjust the
//                input box height when there are excessive Filters"; Jess, 9 Oct — the
//                scrolling variant tried in between was dropped). The visible count is
//                measured, not guessed: every chip is also rendered once in an invisible
//                zero-height row, and the widest prefix that fits next to "+N" and the
//                chevron is shown.
//
// Open state is the parent's when `isOpen`/`onToggle`/`onClose` are passed (so a
// drawer can keep one field open at a time); otherwise the field manages its own.
// Options stay raw strings in and out — the value sent to the backend must remain
// exactly what it stores — `renderLabel` only changes what is displayed/matched.

const CHIP_YELLOW = '#FFEB00';
const FONT = 'Montserrat';
const CHIP_GAP = 6;
const COUNTER_W = 34;      // "+99" worst case
const CHEVRON_W = 28;      // icon + its right padding

export default function MultiSelectChips({
    options,
    selected,
    onChange,
    placeholder = 'Select...',
    searchable = false,
    isOpen,
    onToggle,
    onClose,
    renderLabel = (o) => o,
    fixedHeight = false,
    disabled = false,
}) {
    const wrapperRef = useRef(null);
    const rowRef = useRef(null);
    const measureRefs = useRef([]);
    const [query, setQuery] = useState('');
    const [selfOpen, setSelfOpen] = useState(false);
    const [visibleCount, setVisibleCount] = useState(selected.length);
    const fixed = fixedHeight === true ? 40 : (fixedHeight || 0);

    const controlled = typeof isOpen === 'boolean';
    const open = controlled ? isOpen : selfOpen;
    const close = controlled ? onClose : () => setSelfOpen(false);
    const toggleOpen = controlled ? onToggle : () => setSelfOpen(o => !o);

    useEffect(() => {
        if (!open) return undefined;
        const onDocDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) close && close();
        };
        document.addEventListener('mousedown', onDocDown);
        return () => document.removeEventListener('mousedown', onDocDown);
    }, [open, close]);

    useEffect(() => { if (!open) setQuery(''); }, [open]);

    // fixedHeight: how many chips fit on the one line, keeping room for "+N".
    useLayoutEffect(() => {
        if (!fixedHeight) return undefined;
        const row = rowRef.current;
        if (!row) return undefined;
        const compute = () => {
            const avail = row.clientWidth - CHEVRON_W;
            const widths = measureRefs.current.slice(0, selected.length).map(el => (el ? el.offsetWidth : 0));
            let used = 0;
            let n = 0;
            for (let i = 0; i < widths.length; i += 1) {
                const w = widths[i] + (i ? CHIP_GAP : 0);
                const needCounter = i < widths.length - 1;      // not the last chip → "+N" may follow
                if (used + w + (needCounter ? CHIP_GAP + COUNTER_W : 0) > avail) break;
                used += w;
                n += 1;
            }
            setVisibleCount(n);
        };
        compute();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(compute);
        ro.observe(row);
        return () => ro.disconnect();
    }, [fixedHeight, selected, options]);

    const toggle = (value) => {
        if (disabled) return;
        onChange(selected.includes(value)
            ? selected.filter(v => v !== value)
            : [...selected, value]);
    };

    const visible = query
        ? options.filter(o => String(renderLabel(o)).toLowerCase().includes(query.toLowerCase()))
        : options;

    // `measureRef` set → this is the invisible measuring copy: no remove button.
    const chip = (value, measureRef) => (
        <span
            key={value}
            ref={measureRef}
            style={{
                background: CHIP_YELLOW,
                color: '#212529',
                borderRadius: 16,
                padding: fixedHeight ? '3px 8px 3px 10px' : '4px 10px',
                display: 'inline-flex',
                alignItems: 'center',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: FONT,
                lineHeight: fixedHeight ? '16px' : undefined,
                whiteSpace: 'nowrap',
                flexShrink: 0,
            }}
        >
            {renderLabel(value)}
            <button
                type="button"
                tabIndex={measureRef ? -1 : undefined}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => { e.stopPropagation(); if (!measureRef) onChange(selected.filter(v => v !== value)); }}
                aria-label={`Remove ${renderLabel(value)}`}
                style={{ background: 'none', border: 'none', marginLeft: fixedHeight ? 4 : 6, cursor: 'pointer', padding: 0, display: 'flex', color: '#212529' }}
            >
                <X size={fixedHeight ? 12 : 14} strokeWidth={3} />
            </button>
        </span>
    );

    const shown = fixedHeight ? selected.slice(0, visibleCount) : selected;
    const hidden = fixedHeight ? Math.max(0, selected.length - visibleCount) : 0;

    return (
        <div ref={wrapperRef} style={{ position: 'relative', marginTop: fixedHeight ? 0 : 6 }}>
            {fixedHeight && (
                <>
                    <style>{`
                        /* Options panel: a 4px grey bar, no arrow buttons (Chrome/Edge/Safari).
                           The standard scrollbar-width/-color must NOT be set alongside, or
                           Chrome 121+ ignores the ::-webkit-scrollbar rules and draws arrows. */
                        .msc-scroll::-webkit-scrollbar { width: 4px; height: 4px; }
                        .msc-scroll::-webkit-scrollbar-track { background: transparent; }
                        .msc-scroll::-webkit-scrollbar-thumb { background: #CED4DA; border-radius: 4px; }
                        .msc-scroll::-webkit-scrollbar-button { display: none; width: 0; height: 0; }
                        /* Firefox only */
                        @supports not selector(::-webkit-scrollbar) {
                            .msc-scroll { scrollbar-width: thin; scrollbar-color: #CED4DA transparent; }
                        }
                    `}</style>
                    {/* Invisible measuring row: the same chips, same font, zero height. */}
                    <div aria-hidden="true" style={{ position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden', whiteSpace: 'nowrap', display: 'flex', gap: CHIP_GAP, pointerEvents: 'none' }}>
                        {selected.map((v, i) => chip(v, (el) => { measureRefs.current[i] = el; }))}
                    </div>
                </>
            )}
            <div
                ref={rowRef}
                style={fixedHeight ? {
                    // Figma: fixed height, 10px side padding, radius 8, 1px #6C757D, white.
                    height: fixed,
                    boxSizing: 'border-box',
                    width: '100%',
                    borderRadius: 8,
                    border: '1px solid #6C757D',
                    padding: '0 36px 0 10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: CHIP_GAP,
                    overflow: 'hidden',
                    background: disabled ? '#F8F9FA' : '#fff',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    position: 'relative',
                } : {
                    width: '100%',
                    minHeight: 44,
                    borderRadius: 8,
                    border: '1px solid #6C757D',
                    padding: '6px 30px 6px 10px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 6,
                    background: '#fff',
                    cursor: 'pointer',
                    position: 'relative',
                }}
                onMouseDown={(e) => { e.stopPropagation(); if (fixedHeight) e.preventDefault(); if (!disabled) toggleOpen && toggleOpen(); }}
            >
                {shown.map(value => chip(value))}
                {hidden > 0 && (
                    <span style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: '#6C757D', whiteSpace: 'nowrap', flexShrink: 0 }}>+{hidden}</span>
                )}

                {searchable && open ? (
                    <input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        placeholder={selected.length ? '' : placeholder}
                        style={{
                            flex: 1,
                            minWidth: 80,
                            border: 'none',
                            outline: 'none',
                            fontSize: 14,
                            fontFamily: FONT,
                            color: '#212529',
                            background: 'transparent',
                        }}
                    />
                ) : selected.length === 0 && (
                    <span style={{ color: '#6C757D', fontSize: 14, fontFamily: FONT, lineHeight: '18px' }}>
                        {placeholder}
                    </span>
                )}

                <ChevronDown
                    size={16}
                    style={{
                        position: 'absolute',
                        right: 10,
                        top: '50%',
                        transform: open ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                        color: fixedHeight ? '#212529' : '#6C757D',
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer',
                        pointerEvents: 'none',
                    }}
                />
            </div>

            {open && (
                <div
                    className={fixedHeight ? 'msc-scroll' : undefined}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => { if (fixedHeight) e.preventDefault(); }}
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        border: '1px solid #e9ecef',
                        borderRadius: 8,
                        maxHeight: 220,
                        overflowY: 'auto',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        zIndex: 9999,
                    }}
                >
                    {visible.length === 0 ? (
                        <div style={{ padding: 10, fontSize: 14, fontFamily: FONT, color: '#6C757D' }}>
                            {options.length === 0 ? 'No options available' : 'No matches'}
                        </div>
                    ) : visible.map(value => {
                        const checked = selected.includes(value);
                        return (
                            <div
                                key={value}
                                onClick={() => toggle(value)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: 10,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontFamily: FONT,
                                    backgroundColor: checked ? '#FFFEF0' : 'transparent',
                                }}
                            >
                                <span style={{ color: '#212529', fontWeight: 400 }}>{renderLabel(value)}</span>
                                {checked && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M16.6663 5L7.49967 14.1667L3.33301 10" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
